import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    domainControlsQuestionsService,
    submit_framework_question_answer,
    check_with_nova,
    upload_document_to_check_with_nova,
} from '../../services';
import ScreenLoader from '../../Components/ScreenLoader';
import QuestionCard from './QuestionCard';
import ControlQuestionCard from './ControlQuestionCard';
import '../../styles/AddOns.scss';

function BankUserControlQuestions() {
    const { frameworkId, domainId, controlId } = useParams();
    const navigate = useNavigate();

    const [questions, setQuestions] = useState([]);
    const [controlName, setControlName] = useState('');
    const [domainName, setDomainName] = useState('');
    const [organizationDomainId, setOrganizationDomainId] = useState(null);
    const [screenLoader, setScreenLoader] = useState(true);
    const [responses, setResponses] = useState({});
    const [checkingId, setCheckingId] = useState(null);
    const [error, setError] = useState('');
    const [resMsg, setResMsg] = useState('');
    const [messageQuestionId, setMessageQuestionId] = useState(null);
    const [loading, setLoading] = useState(false);

    // Fetch questions for this specific control
    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                setScreenLoader(true);
                const params = { domain_id: domainId };
                const response = await domainControlsQuestionsService(params);
                const domainData = response?.statusCode?.response_data?.data || {};

                // Find the matching control
                let foundQuestions = [];
                let foundControlName = '';
                let foundDomainName = domainData?.domain?.name || '';
                let foundOrgDomainId = domainData?.organization_domain_id;

                for (const control of domainData?.controls || []) {
                    const cId = String(control?.organization_domain_control_id || control?.control_id || control?.id || '');
                    if (cId === String(controlId)) {
                        foundControlName = control?.control?.control_title || control?.control_title || '';
                        foundQuestions = control?.questions || [];
                        break;
                    }
                }

                setQuestions(foundQuestions);
                setControlName(foundControlName || localStorage.getItem('selectedControlName') || 'Control');
                setDomainName(foundDomainName || localStorage.getItem('selectedDomainName') || 'Domain');
                setOrganizationDomainId(foundOrgDomainId);

                // Initialize responses from existing answers
                const initialResponses = {};
                foundQuestions.forEach((q) => {
                    const qid = q?.control_question_id;
                    if (!qid) return;

                    const questionAnswerBlock = q?.question_answer || q?.answer_details || {};
                    const existingAnswer =
                        q?.answer_response || q?.user_response ||
                        questionAnswerBlock?.answer_response || questionAnswerBlock?.user_response || '';
                    const existingComments =
                        q?.user_answer || q?.answer ||
                        questionAnswerBlock?.user_answer || questionAnswerBlock?.answer || questionAnswerBlock?.comments || '';
                    const evidenceSource =
                        q?.evidence_submitted || q?.evidences ||
                        questionAnswerBlock?.evidence_submitted || questionAnswerBlock?.evidences || [];
                    const evidenceUrls = evidenceSource?.map((e) => e?.url)?.filter(Boolean) || [];

                    if ((existingAnswer && existingAnswer.trim() !== '') ||
                        (existingComments && existingComments.trim() !== '') ||
                        evidenceUrls.length > 0) {
                        initialResponses[qid] = {
                            answer: existingAnswer,
                            comments: existingComments,
                            file_urls: evidenceUrls,
                            files: [],
                            submitted: true
                        };
                    }
                });
                setResponses(initialResponses);
            } catch (err) {
                console.error('Error fetching questions:', err);
            } finally {
                setScreenLoader(false);
            }
        };

        if (frameworkId && domainId && controlId) fetchQuestions();
    }, [frameworkId, domainId, controlId]);

    const updateResponse = useCallback((id, field, value) => {
        setResponses((prev) => {
            if (prev[id]?.[field] === value) return prev;
            return {
                ...prev,
                [id]: { ...prev[id], [field]: value, submitted: false },
            };
        });
        setError('');
        setResMsg('');
        setMessageQuestionId(null);
    }, []);

    const handleFileUpload = useCallback(async (id, file, orgId) => {
        if (!file) return;
        setResponses((prev) => ({
            ...prev,
            [id]: { ...prev[id], files: [...(prev[id]?.files || []), file] }
        }));
        setError('');

        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_identifier', id);
        formData.append('organization_id', orgId);

        try {
            const upload = await upload_document_to_check_with_nova(formData);
            const url = upload?.statusCode?.response_data?.data?.s3_url || '';
            const user_document_id = upload?.statusCode?.response_data?.data?.user_document_id || '';
            setResponses((prev) => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    user_document_id,
                    file_urls: [...(prev[id]?.file_urls || []), url]
                }
            }));
        } catch (err) {
            console.error('File Upload Failed:', err);
            setResponses((prev) => ({
                ...prev,
                [id]: { ...prev[id], files: (prev[id]?.files || []).filter(f => f !== file) }
            }));
        }
    }, []);

    const removeFile = useCallback((id, index) => {
        setResponses((prev) => {
            const { files = [], file_urls = [] } = prev[id] || {};
            return {
                ...prev,
                [id]: {
                    ...prev[id],
                    files: files.filter((_, i) => i !== index),
                    file_urls: file_urls.filter((_, i) => i !== index)
                }
            };
        });
    }, []);

    const checkWithNovaHandler = useCallback(async (questionId) => {
        const selected = responses[questionId];
        setCheckingId(questionId);
        if (!selected?.comments) {
            setCheckingId(null);
            return;
        }
        const params = { question_id: questionId, answer: selected.comments, files: selected.file_urls || [] };
        try {
            await check_with_nova(params);
        } catch (err) {
            console.error(err);
        } finally {
            setCheckingId(null);
        }
    }, [responses]);

    const submitQuestionAnswerHandler = useCallback(async (questionId) => {
        try {
            setLoading(true);
            setError('');
            setResMsg('');
            setMessageQuestionId(questionId || null);

            const resp = responses[questionId];
            if (!resp) {
                setError('Please select Yes / No / Partial before saving.');
                setLoading(false);
                return;
            }

            const hasAnswer = resp.answer && resp.answer.trim() !== '';
            const hasComments = resp.comments && resp.comments.trim() !== '';
            const validAnswers = ['Yes', 'No', 'Partial'];

            if (!hasAnswer || !validAnswers.includes(resp.answer)) {
                setError('Please select Yes / No / Partial before saving.');
                setLoading(false);
                return;
            }

            const evidences = (resp.file_urls || []).map((url) => ({ url }));
            const answerValue = hasComments ? resp.comments : (hasAnswer ? resp.answer : '');

            const payload = {
                organization_domain_id: organizationDomainId || null,
                question_id: questionId,
                user_document_id: resp.user_document_id || null,
                answer: answerValue,
                user_response: hasAnswer ? resp.answer : '',
                evidences: evidences.length > 0 ? evidences : [{ url: '' }]
            };

            if (!payload.organization_domain_id || !payload.question_id || !payload.answer) {
                setError('Missing required fields.');
                setLoading(false);
                return;
            }

            const response = await submit_framework_question_answer(payload);
            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.data?.message || response?.message;
            const errorMessage =
                response?.errors?.message || response?.error?.message || response?.data?.error || null;

            if (successMessage) {
                setResMsg(successMessage);
                setError('');
                setMessageQuestionId(questionId);
                setResponses((prev) => ({
                    ...prev,
                    [questionId]: { ...prev[questionId], submitted: true }
                }));
            } else if (errorMessage) {
                setError(errorMessage);
                setResMsg('');
                setMessageQuestionId(questionId);
            }
        } catch (err) {
            console.error('Submit Error:', err);
            setError('An error occurred while submitting.');
            setResMsg('');
            setMessageQuestionId(questionId);
        } finally {
            setLoading(false);
        }
    }, [responses, organizationDomainId]);

    if (screenLoader) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <ScreenLoader />
            </div>
        );
    }

    return (
        <div className="questions-page-container">
            {/* Header */}
            <div className="d-flex align-items-center gap-3 mb-4 pt-4">
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => navigate(-1)}
                    style={{ color: '#94A3B8', borderColor: '#334155' }}
                >
                    ← Back
                </button>
                <div>
                    <h3 className="color_white mb-0 font_20 font-600">{controlName}</h3>
                    <small style={{ color: '#64748b' }}>{domainName}</small>
                </div>
            </div>

            {/* Questions */}
            {questions.length === 0 ? (
                <div className="text-center py-5">
                    <p className="text-muted" style={{ fontSize: '16px' }}>No questions found for this control.</p>
                </div>
            ) : (
                <div>
                    {questions.map((q, idx) => {
                        const qid = q?.control_question_id;
                        const questionText = q?.question?.question || q?.question_text || q?.question || '';

                        // Map status based on answer
                        const answer = responses[qid]?.answer || '';
                        let status = 'Pending';
                        if (answer === 'Yes') status = 'Pass';
                        if (answer === 'No') status = 'Fail';
                        if (answer === 'Partial') status = 'Warning';

                        return (
                            <ControlQuestionCard
                                key={qid || idx}
                                title={questionText}
                                description={controlName}
                                itemId={qid}
                                service={domainName}
                                status={status}
                                checkName={q?.question?.technical_id || `check_${qid}`}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default BankUserControlQuestions;

